import * as React from 'react';
import withStyles from 'react-jss';
import { CssType,
  ThemeType} from 'src/theming/jssTypes';
import { CloseOutlined, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { Modal, Input, Checkbox, Tabs, Switch, Select } from 'src/design-system/components/atoms';
import { Space } from 'antd';
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import { DragIcon } from 'src/components/common/icons/DragIcon';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setColumns, setCustomFilters, setCurrentCustomFilters,
  setCustomActions, setCustomUpdates, reloadBucket } from 'src/actions/genericConsignmentActions';
import classNames from 'classnames';
import { trackGaEvents } from 'src/utils/googleAnalyticsHelper';
import { CUSTOMISE_COLUMNS, gaType } from 'src/utils/gaConstants';
import { withTranslation } from 'react-i18next';

const Search = Input.Search;
const TabPane = Tabs.TabPane;
const Option = Select.Option;

const autoRefreshOptions = [
  {name: '10 Seconds', value: 10},
  {name: '30 Seconds', value: 30},
  {name: '45 Seconds', value: 45},
  {name: '1 minute', value: 60},
];
const styles = (theme: ThemeType): CssType => ({
  listItemStyle: {
    marginTop: theme.spacing[2],
    backgroundColor: theme.color.semantic.brand.default.light,
    borderRadius: theme.radius.sm,
    padding: theme.spacing[1],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
    width: '100%',
    fontSize: theme.typography.fontSize.sm,
  },
  mainDiv: {
    height: '350px',
    display: 'flex',
    overflow: 'hidden',
    justifyContent: 'space-between',
  },
  leftDiv: {
    width: '55%',
    display: 'flex',
    flexDirection: 'column',
    '& .ant-checkbox-group-item': {
      marginBottom: theme.spacing[3],
    },
    '& .ant-checkbox-group': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  searchStyle: {
  },
  rightDiv: {
    overflow: 'auto',
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    padding: theme.spacing[3],
    width: '48%',
    '& ul': {
      padding: theme.spacing[0],
      'list-style-type': 'none',
    },
  },
  checkboxDiv: {
    overflow: 'auto',
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    height: '100%',
    padding: theme.spacing[4],
  },
  customHeader: {
    fontWeight: theme.typography.fontWeight.semibold,
    fontSize: theme.typography.fontSize.md,
    height: '10%',
    color: theme.color.semantic.text.primary,
    padding: theme.spacing[1],
    borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    marginBottom: theme.spacing[3],
    display: 'flex',
    justifyContent: 'space-between',
  },
  listStyle: {
    height: '90%',
    overflow: 'auto',
  },
  customRightDiv: {
    width: '60%',
    overflow: 'hidden',
  },
  updateDiv: {
    height: '350px',
    overflow: 'hidden',
    paddingTop: theme.spacing[3],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  customPrimaryButton: {
    backgroundColor: theme.color.semantic.brand.default.primary,
    color: theme.color.semantic.text.inverse,
  },
  headerContainer: {
    marginBottom: theme.spacing[4],
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    borderRadius: theme.radius.sm,
    padding: `${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[0]} ${theme.spacing[3]}`,
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
  },
  headerTitle: {
    fontWeight: theme.typography.fontWeight.bold,
    display: 'block',
    padding: `${theme.spacing[0]} ${theme.spacing[2]}`,
    borderRadius: `${theme.radius.sm} ${theme.radius.sm} ${theme.radius.none} ${theme.radius.none}`,
  },
});

class CustomColumnsModal extends React.PureComponent<any, any> {

  state: any = {
    isUploading: false,
    items: [], // Contains value or column_id of selected or existing columns
    allOptions : [],
    allFilters: [],
    existingFilters: [],
    allActions: [],
    existingActions: [],
    updates: [],
    enableUpdateForAll: false,
    existingHeaders: [],
    existingChildHeaders: [],
    allHeaders: [],
    headerChildMap: {},
    childHeaderMap: {},
    expandedHeaders: {},
  };

  componentDidMount() {
    const { showFiltersAndActions, fullFiltersMetadata,
      filtersToShow, fullActionsList, updatesToShow, actionsToShow,
      fullColumnList, columnsToShow, headersToShow, fullHeadersList, t, maxColumnLimit } = this.props;

    const expandedHeaders = {};
    if (fullHeadersList) {
      const headerChildMap = {};
      const childHeaderMap = {};

      fullHeadersList.forEach(header => {
        if (header.is_header) {
          headerChildMap[header.key] = [];
          expandedHeaders[header.key] = true;
        }
        if (header.parent_header) {
          const parentId = header.parent_header;
          if (!headerChildMap[parentId]) {
            headerChildMap[parentId] = [];
          }
          headerChildMap[parentId].push(header.key);
          childHeaderMap[header.key] = parentId;
        }
      });
      this.setState({ headerChildMap, childHeaderMap });
    }

    const allOptions = fullColumnList.map((ele) => {
      return (this.isColumnDisabled(ele.key || ele.id) ?
      { label: t(ele.pretty_name), value: ele.key || ele.id, disabled: true } :
      { label: t(ele.pretty_name), value: ele.key || ele.id });
    });
    const MAX_COLUMNS_SELECTED = maxColumnLimit;
    const existingColumns = columnsToShow.map((ele) => {
      return (ele.key || ele.id);
    });
    this.setState({
      allOptions,
      items:existingColumns.slice(0, MAX_COLUMNS_SELECTED),
    });
    if(showFiltersAndActions){
      this.setState({
        allFilters: this.getAllOptionsList(fullFiltersMetadata),
        existingFilters: this.getExistingOptionsList(filtersToShow),
        allActions: this.getAllOptionsList(fullActionsList),
        existingActions: this.getExistingOptionsList(actionsToShow),
        allHeaders: this.getAllHeadersList(fullHeadersList),
        existingHeaders: this.getExistingOptionsList(headersToShow),
        updates: updatesToShow,
        expandedHeaders
      }, this.updateExistingChildHeaders);
    }
  }

  updateExistingChildHeaders = () => {
    const { fullHeadersList, passNestedFields } = this.props;
    const { existingHeaders, headerChildMap } = this.state;
    if (!fullHeadersList || !existingHeaders || !passNestedFields) return;

    const childHeaders = existingHeaders.filter(header => {
      const headerInfo = fullHeadersList.find(h => h.key === header.value);
      return !(headerInfo && headerInfo.is_header === true);
    });

    // Deselect Parent headers where all children are deselected
    const updatedExistingHeaders = existingHeaders.filter(header => {
      const childIds = headerChildMap[header.value] || [];
      const areChildrenSelected = childIds.some(childId => existingHeaders.find(h => h.value === childId));

      if (childIds.length > 0 && !areChildrenSelected) {
        return false;
      }
      return true
    });

    this.setState({
      existingChildHeaders: childHeaders,
      existingHeaders: updatedExistingHeaders
    });
  };
  toggleHeaderExpand = (headerId) => {
    this.setState(prevState => ({
      expandedHeaders: {
        ...prevState.expandedHeaders,
        [headerId]: !prevState.expandedHeaders[headerId]
      }
    }));
  }

  disabledTripColumns = ['organisation_reference_number', 'status'];
  getAllHeadersList = (headers) => {
    const { t } = this.props;
    return headers.map((ele) => {
      return { label: t(ele.pretty_name), value: ele.key, key:ele.key, is_header: ele.is_header, parent_header: ele.parent_header };
    });
  };
  getAllOptionsList = (options) => {
    const { t } = this.props;
    return options.map((ele) => {
      return { label: t(ele.label), value: ele.key };
    });
  };
  getExistingOptionsList = (options) => {
    return options.map(ele => {
      return { value: ele.key, visibleOutside: ele.show_on_main_dashboard };
    });
  };

  isColumnDisabled(column_id) {
    const { viewType } = this.props;
    const tripViewTypes = ['retailTripsPrimary', 'retailTripsSecondary'];
    if( column_id === 'reference_number' ||
        (column_id === 'organisation_reference_number' && tripViewTypes.includes(viewType))
     ){
      return true;
    }
    return false;
  }

  ListItem = (record: any) => {
    const { classes, t, theme } = this.props;
    return (
      <div className={classes.listItemStyle}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
        { this.isColumnDisabled(record.record.value) ? null : <DragIcon /> }
        <span style={{ marginLeft: theme.spacing[2] }}>
            {t(this.props.fullColumnList.filter(
              x => (x.key || x.id) === record.record.value)[0]?.pretty_name)}
          </span>
        </div>
        { this.isColumnDisabled(record.record.value) ? null :
          <div>
            <CloseOutlined onClick={() => this.handleCrossClick(record.record.value)} />
          </div> }
      </div>
    );
  };

  handleCrossClick = (value) => {
    this.setState({
      items: this.state.items.filter(x => x !== value),
    });
  };
  handleFilterCrossClick = (value) => {
    const { existingFilters } = this.state;
    this.setState({
      existingFilters: existingFilters.filter(x => x.value !== value),
    });
  };
  handleActionCrossClick = (value) => {
    const { existingActions } = this.state;
    this.setState({
      existingActions: existingActions.filter(x => x.value !== value),
    });
  };
  handleHeaderCrossClick = (value) => {
    const { existingHeaders, headerChildMap, childHeaderMap } = this.state;
    
    const isParent = headerChildMap.hasOwnProperty(value);
    const isChild = childHeaderMap.hasOwnProperty(value);
    
    const newHeaders = existingHeaders.filter(({ value: val }) => {
      if (val === value) return false;
    
      if (isParent) {
        const childIds = headerChildMap[value] || [];
        return !childIds.includes(val);
      }
    
      if (isChild) {
        const parentId = childHeaderMap[value];
        return val !== parentId;
      }
    
      return true;
    });
    this.setState({ existingHeaders: newHeaders }, this.updateExistingChildHeaders);
  };

  SortableItem = SortableElement((props: any) => <this.ListItem record={props} />);
  SortableList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return this.isColumnDisabled(value) ?
          <this.SortableItem key={`item-${index}`} index={index} value={value} disabled={true}/> :
          <this.SortableItem key={`item-${index}`} index={index} value={value}/>;
        })
        }
      </ul>
    );
  });

  onfilterOrActionLocationChange = (checked, value, itemType) => {
    let options = this.getExistingOptions(itemType);
    options = options.map((ele) => {
      if(value === ele.value){
        ele.visibleOutside = checked;
      }
      return ele;
    });
    this.setExistingOptions(itemType, options);
  };

  getExistingOptions = (itemType) => {
    const { existingFilters, existingActions, existingHeaders } = this.state;
    switch (itemType) {
      case 'filter':
        return existingFilters;
      case 'header':
        return existingHeaders;
      case 'action':
        return existingActions;
      default:
        return [];
    }
  };
  setExistingOptions = (itemType, options) => {
    switch(itemType){
      case 'filter':
        this.setState({ existingFilters: options });
        break;
      case 'action':
        this.setState({ existingActions: options});
        break;
      case 'header':
        this.setState({ existingHeaders: options}, this.updateExistingChildHeaders);
        break;
      default:
        return;
    }
  };
  getFullOptionsList = (itemType) => {
    const { fullFiltersMetadata, fullActionsList, fullHeadersList } = this.props;
    switch (itemType) {
      case 'filter':
        return fullFiltersMetadata;
      case 'action':
        return fullActionsList;
      case 'header':
        return fullHeadersList;
      default:
        return [];
    }
  };
  getAllOptions = (itemType) => {
    const { allFilters, allActions, allHeaders } = this.state;
    switch(itemType){
      case 'filter':
        return allFilters;
      case 'action':
        return allActions;
      case 'header':
        return allHeaders;
      default:
        return [];
    }
  };
  setAllOptions = (itemType, options) => {
    switch(itemType){
      case 'filter':
        this.setState({ allFilters: options });
        break;
      case 'action':
        this.setState({ allActions: options});
        break;
      case 'header':
        this.setState({ allHeaders: options});
        break;
      default:
        return;
    }
  };

  getFilterAndActionListItem = (record, itemType) => {
    const { classes, t, allowMainDashboardSwitch, theme } = this.props;
    const allOptions = this.getFullOptionsList(itemType);
    let item = null;
    switch(itemType){
      case 'filter':
        item = record.filterItem.filter;
        break;
      case 'action':
        item = record.actionItem.action;
        break;
      case 'header':
        item = record.headerItem.header;
        break;
      default:
        return;
    }

    return (
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <div className={classes.listItemStyle}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {this.isHeaderDisabled(item.value) ? null: <DragIcon />}
            <span style={{ marginLeft: theme.spacing[2] }}>
              {t(allOptions.filter(x => x.key === item.value)[0]?.label)}
            </span>
          </div>
          <div>
            {this.isHeaderDisabled(item.value) ? null:  <CloseOutlined onClick={() => {
              switch(itemType){
                case 'filter':
                  this.handleFilterCrossClick(item.value);
                  break;
                case 'action':
                  this.handleActionCrossClick(item.value);
                  break;
                case 'header':
                  this.handleHeaderCrossClick(item.value);
                  break;
                default:
                  return;
              }
            }} />}
          </div>
        </div>
        {allowMainDashboardSwitch && (<div style={{width: '40%', textAlign: 'center'}}>
          <Switch
            size="small"
            checked={item.visibleOutside}
            disabled={this.isHeaderDisabled(item.value)}
            style={{ marginTop: theme.spacing[2] }}
            onChange={(e) => this.onfilterOrActionLocationChange(e, item.value, itemType)} />
        </div>)}
      </div>
    );
  };
  isHeaderDisabled(column_id) {
    if(this.disabledTripColumns.includes(column_id))
    {
      return true;
    }
    return false;
  }
  FilterListItem = (record: any) => this.getFilterAndActionListItem(record, 'filter');
  SortableFilterItem = SortableElement((props: any) => {
    return <this.FilterListItem filterItem={props} />;
  });
  SortableFilterList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return <this.SortableFilterItem key={`item-${index}`} index={index} filter={value}/>;
        })}
      </ul>
    );
  });

  ActionListItem = (record: any) => this.getFilterAndActionListItem(record, 'action');
  SortableActionItem = SortableElement((props: any) => {
    return <this.ActionListItem actionItem={props} />;
  });
  SortableActionList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return <this.SortableActionItem key={`item-${index}`} index={index} action={value}/>;
        })}
      </ul>
    );
  });

  HeaderListItem = (record: any) => this.getFilterAndActionListItem(record, 'header');

  SortableHeaderItem = SortableElement((props: any) => {
    return <this.HeaderListItem headerItem={props} />;
  });
  SortableHeaderList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return this.isHeaderDisabled(value?.value) ? <this.SortableHeaderItem key={`item-${index}`} index={index} header={value} disabled={true}/>
            : <this.SortableHeaderItem key={`item-${index}`} index={index} header={value}/>;
        })}
      </ul>
    );
  });

  onSortEnd = ({ oldIndex, newIndex }) => {
    if (newIndex !== 0) {
      this.setState(({ items }) => ({
        items: arrayMove(items, oldIndex, newIndex),
      }));
    }
  };

  onFilterSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(({ existingFilters }) => ({
      existingFilters: arrayMove(existingFilters, oldIndex, newIndex),
    }));
  };

  onActionSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(({ existingActions }) => ({
      existingActions: arrayMove(existingActions, oldIndex, newIndex),
    }));
  };

  onHeaderSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(({ existingHeaders }) => ({
      existingHeaders: arrayMove(existingHeaders, oldIndex, newIndex),
    }));
  };

  onNestedHeaderSortEnd = ({ oldIndex, newIndex, collection }) => {
    const parentHeaderId = collection;
    if (parentHeaderId && oldIndex !== newIndex) {
      this.setState(prevState => {
        const { headerChildMap, existingChildHeaders } = prevState;
        const existingHeaderChildMap = {};

        existingChildHeaders.forEach(({ value }) => {
          for (const [header, children] of Object.entries(headerChildMap)) {
            if ((children as any)?.includes(value)) {
              existingHeaderChildMap[header] = existingHeaderChildMap[header] || [];
              existingHeaderChildMap[header].push(value);
              break;
            }
          }
        });

        const childrenArray = existingHeaderChildMap[parentHeaderId] || [];
        const updatedChildrenArray = arrayMove([...childrenArray], oldIndex, newIndex);
        existingHeaderChildMap[parentHeaderId] = updatedChildrenArray;
        
        const childHeadersMap = existingChildHeaders.reduce((map, header) => {
          map[header.value] = header;
          return map;
        }, {});

        const updatedExistingHeaders = [];

        Object.keys(existingHeaderChildMap).forEach(parentId => {
          const children = existingHeaderChildMap[parentId] || [];
          updatedExistingHeaders.push({ value: parentId });

          children.forEach(childId => {
            if (childHeadersMap[childId]) {
              updatedExistingHeaders.push(childHeadersMap[childId]);
            }
          });
        });
        return {
          existingHeaders: updatedExistingHeaders
        };
      }, this.updateExistingChildHeaders);
    }
  };

  handleModalOk = async () => {
    const { showFiltersAndActions, fullColumnList, setCustomFilters, setColumns, setCustomActions,
      setCurrentCustomFilters, hideActions, setCustomUpdates,
      onModalSubmit, autoRefreshTrip, handleRefresh, bucket, reloadBucket, currentActiveTab } = this.props;
    const { items, existingFilters, existingActions, updates,
      enableUpdateForAll, existingHeaders, existingChildHeaders  } = this.state;

    this.setState({ isUploading: true });
    const columns = items.map((element) => {
      return fullColumnList.filter(x => ((x.key || x.id) === element))[0];
    }).filter(col => col);
    trackGaEvents({actionType: CUSTOMISE_COLUMNS}, {bucket});
    if(onModalSubmit) {
      const actions = this.getCustomOptionsList(existingActions);
      const headers = this.getCustomOptionsList(existingChildHeaders);
      const result = await onModalSubmit(columns, actions, headers, updates);
      if (result && result.isSuccess) {
        this.handleModalClose();
        await handleRefresh();
      } else {
        this.setState({ isUploading: false });
      }
      return;
    }
    await setColumns(columns);
    if(showFiltersAndActions){
      const customFilters = this.getCustomOptionsList(existingFilters);
      const customActions = this.getCustomOptionsList(existingActions);
      setCustomFilters(customFilters);
      setCurrentCustomFilters(customFilters);
      !hideActions && setCustomActions(customActions);
      setCustomUpdates({updates, enableUpdateForAll });
    }
    if (reloadBucket) {
      reloadBucket();
    }
    this.handleModalClose();
  };
  getCustomOptionsList = (options) => {
    const { fullColumnList } = this.props;
    const filteredHeaders = options.map((option) => {
      return fullColumnList.filter(x => ((x.key) === option.value))[0];
    }).filter(col => col);


    return filteredHeaders;
  };

  handleModalClose = () => {
    this.props.onModalClose();
  };

  handleSearchChange = (e) => {
    const { t } = this.props;
    this.setState({
      allOptions: this.props.fullColumnList
        .filter(x =>
          x.pretty_name
            .toLowerCase()
            .includes(e.target.value.toLowerCase()),
        )
        .map((ele) => {
          return ( this.isColumnDisabled(ele.key || ele.id) ?
            { label: t(ele.pretty_name), value: ele.key || ele.id, disabled: true } :
            { label: t(ele.pretty_name), value: ele.key || ele.id });
        },
        ),
    });
  };

  onFilterOrActionSearch = (e, searchType) => {
    const { fullHeadersList } = this.props;
    const optionsList = this.getFullOptionsList(searchType);

    if (searchType === 'header') {
      const searchText = e.target.value.toLowerCase();

      const filteredHeaders = fullHeadersList.filter(header => {
        const headerText = (header.label || '').toLowerCase();
        return headerText.includes(searchText);
      });

      this.setAllOptions(searchType, filteredHeaders.map(header => ({
        label: header.label,
        value: header.key || header.id,
        is_header: header.is_header,
        parent_header: header.parent_header
      })));
      return;
    }

    const searchedOptions = optionsList.filter(x => {
      return x.label?.toLowerCase()?.includes(e.target.value.toLowerCase());
    }).map(ele => {
      return { label: ele.label, value: ele.key };
    });
    this.setAllOptions(searchType, searchedOptions);
  };
  handleCheckboxChange = (e) => {
    if (e.target.checked) {
      this.setState({ items: [...this.state.items, e.target.value] });
    } else {
      const items = this.state.items.filter(x => x !== e.target.value);
      this.setState({ items });
    }
  };
  onFilterOrActionCheckboxChange = (e, searchType) => {
    const existingOptions = this.getExistingOptions(searchType);
    if (e.target.checked) {
      this.setExistingOptions(searchType, [...existingOptions, { value: e.target.value }]);
    } else {
      const optionsToShow = existingOptions.filter(x => x.value !== e.target.value);
      this.setExistingOptions(searchType, optionsToShow);
    }
  };

  handleNestedHeaderCheckboxChange = (e, headerId, childIds) => {
    const { checked } = e.target;
    const { existingHeaders } = this.state;

    let newHeaders = [...existingHeaders];
    if (checked) {
      if (newHeaders.findIndex(h => h.value === headerId) === -1) {
        newHeaders.push({ value: headerId, visibleOutside: false });
      }

      childIds.forEach(childId => {
        if (newHeaders.findIndex(h => h.value === childId) === -1) {
          newHeaders.push({ value: childId, visibleOutside: false });
        }
      });
    } else {
      newHeaders = newHeaders.filter(h => h.value !== headerId);
      newHeaders = newHeaders.filter(h => !childIds.includes(h.value));
    }

    this.setState({ existingHeaders: newHeaders }, this.updateExistingChildHeaders);
  };
  
  handleNestedHeaderChildCheckboxChange = (e, childId, headerId) => {
    const { checked } = e.target;
    const { existingHeaders, headerChildMap } = this.state;
    const { fullHeadersList } = this.props;

    let newHeaders = [...existingHeaders];

    if (checked) {
      if (newHeaders.findIndex(h => h.value === childId) === -1) {
        newHeaders.push({ value: childId, visibleOutside: false });
      }

      const fullChildIdsForParent = fullHeadersList.filter(opt =>
        opt.parent_header === headerId
      ).map(child => child.key);

      const selectedChildrenCount = fullChildIdsForParent.reduce((count, id) => {
        return newHeaders.findIndex(h => h.value === id) !== -1 ? count + 1 : count;
      }, 0);

      if (selectedChildrenCount === fullChildIdsForParent.length) {
        if (newHeaders.findIndex(h => h.value === headerId) === -1) {
          newHeaders.push({ value: headerId, visibleOutside: false });
        }
      }
    } else {
      newHeaders = newHeaders.filter(h => h.value !== childId);
      newHeaders = newHeaders.filter(h => h.value !== headerId);
    }

    this.setState({ existingHeaders: newHeaders }, this.updateExistingChildHeaders);
  };

  renderColumns = () => {
    const { classes,t, maxColumnLimit, theme } = this.props;
    const MAX_COLUMNS_SELECTED = maxColumnLimit;
    if (this.state.items?.length > MAX_COLUMNS_SELECTED) {
      this.setState({
        items: this.state.items?.slice(0, MAX_COLUMNS_SELECTED),
      });
    }
    let totalCheckedColumns = 0;
    return (
      <div className={classes.mainDiv}>
        <div className={classes.leftDiv} >
          <div style={{
            padding: theme.spacing[3], border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}` }}>
            <Search
              onChange={this.handleSearchChange}
              className={classes.searchStyle}
              placeholder={t('search')}
            />
          </div>
          <div
            style={{ padding: theme.spacing[4], color: theme.color.semantic.text.primary, fontWeight: theme.typography.fontWeight.bold }}
          >
            {this.state.items?.length || 0} {t('Selected / Max')} {MAX_COLUMNS_SELECTED} {t('columns allowed')}
          </div>
          <div className={classes.checkboxDiv}>
            {this.state.allOptions.map((x) => {
              let isChecked = this.state.items.includes(x.value);
              let shouldDisable = x.disabled || false;
              if (this.state.items?.length >= MAX_COLUMNS_SELECTED && !isChecked) {
                shouldDisable = true;
              }
              if (totalCheckedColumns >= MAX_COLUMNS_SELECTED) {
                isChecked = false;
                shouldDisable = true;
              }
              if(isChecked){
                totalCheckedColumns++;
              }
              return (
                <div style={{ marginBottom: theme.spacing[3] }} key={x.value}>
                  <Checkbox
                    checked = {isChecked}
                    value={x.value}
                    disabled={shouldDisable}
                    onChange={(e) => { this.handleCheckboxChange(e); }}
                  >
                    {t(x.label)}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
        <div className={classes.rightDiv} >
          <div style={{
            fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.md,
            color: theme.color.semantic.text.secondary,
            marginBottom: theme.spacing[3] }} >
            {this.props.t('existing_columns')}
          </div>
          <div><this.SortableList distance={1} items={this.state.items} onSortEnd={this.onSortEnd}
            helperClass="sortableHelper" />
            </div>
        </div>
      </div>
    );
  };
  renderFiltersAndActions = (itemType) => {
    const { classes, t, allowMainDashboardSwitch, passNestedFields, theme } = this.props;
    if (passNestedFields && itemType === 'header') {
      return this.renderNestedFiltersAndActions(itemType);
    }  
    const { existingFilters, existingActions, existingHeaders } = this.state;
    const allOptions = this.getAllOptions(itemType);
    const existingOptions = this.getExistingOptions(itemType);
    return (
      <div className={classes.mainDiv}>
        <div className={classes.leftDiv}>
          <div style={{
            padding: theme.spacing[3], border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}` }}>
            <Search
              onChange={(e) => this.onFilterOrActionSearch(e, itemType)}
              className={classes.searchStyle}
              placeholder={t('filter_search')}
            />
          </div>
          <div className={classes.checkboxDiv}>
            {allOptions.map((x) => {
              return (
                <div style={{ marginBottom: theme.spacing[3] }} >
                  <Checkbox
                    checked = {existingOptions.findIndex(ele => ele.value === x.value) !== -1}
                    value={x.value}
                    disabled = {this.isHeaderDisabled(x.value) || false}
                    onChange={(e) => this.onFilterOrActionCheckboxChange(e, itemType)} >
                      {t(x.label)}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
        <div className={classNames(classes.rightDiv, classes.customRightDiv)} >
          <div className={classes.customHeader} >
            <div style={{textTransform: 'capitalize'}}>{itemType === 'header' ? t('details_to_show') : t(`${itemType}s_to_show`) }</div>
            <div>{allowMainDashboardSwitch? t('show_on_main_dashboard') : ''}</div>
          </div>
          <div className={classes.listStyle}>
            {
              {
                filter : <this.SortableFilterList distance={1} items={existingFilters}
                onSortEnd={this.onFilterSortEnd} helperClass="sortableHelper" />,
                action : <this.SortableActionList distance={1} items={existingActions}
                onSortEnd={this.onActionSortEnd} helperClass="sortableHelper" />,
                header : <this.SortableHeaderList distance={1} items={existingHeaders}
                onSortEnd={this.onHeaderSortEnd} helperClass="sortableHelper" />,
              }[itemType]
            }
          </div>
        </div>
      </div>
    );
  };

  renderNestedFiltersAndActions = (itemType) => {
    const { classes, t, allowMainDashboardSwitch, fullHeadersList, theme } = this.props;
    const { existingHeaders, existingChildHeaders, headerChildMap, childHeaderMap } = this.state;

    const existingHeaderChildMap = {};
    existingChildHeaders.forEach(({ value }) => {
      for (const [header, children] of Object.entries(headerChildMap)) {
        if ((children as any)?.includes(value)) {
          existingHeaderChildMap[header] = existingHeaderChildMap[header] || [];
          existingHeaderChildMap[header].push(value);
          break;
        }
      }
    });

    const allOptions = this.getAllOptions(itemType);
    const existingOptions = this.getExistingOptions(itemType);

    if (itemType !== 'header') {
      return this.renderFiltersAndActions(itemType);
    }

    const headers = fullHeadersList.filter(option => option.is_header);
    const headersWithoutParent = fullHeadersList.filter(option =>
      !option.is_header && !option.parent_header
    );

    const selectedHeadersSet = new Set(existingHeaders.map(header => header.value));

    const visibleHeaders = new Set();
    const visibleChildrenMap = new Map();

    allOptions.forEach(option => {
      if (option.parent_header) {
        visibleHeaders.add(option.parent_header);

        if (!visibleChildrenMap.has(option.parent_header)) {
          visibleChildrenMap.set(option.parent_header, []);
        }
        visibleChildrenMap.get(option.parent_header).push(option);
      } else {
        visibleHeaders.add(option.value);
      }
    });

    return (
      <div className={classes.mainDiv}>
        <div className={classes.leftDiv}>
          <div style={{
            padding: theme.spacing[3], border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`
          }}>
            <Search
              onChange={(e) => this.onFilterOrActionSearch(e, itemType)}
              className={classes.searchStyle}
              placeholder={t('filter_search')}
            />
          </div>
          <div className={classes.checkboxDiv}>
            {/* Render headers with their children */}
            {headers
            .filter(header => visibleHeaders.has(header.key))
            .map(header => {
              const headerId = header.key;

              const fullChildIdsForParent = fullHeadersList.filter(opt =>
                opt.parent_header === headerId
              ).map(child => child.key);

              const allVisibleChildren = visibleChildrenMap.get(headerId) || [];

              const selectedChildren = fullChildIdsForParent.filter(id =>
                existingOptions.findIndex(opt => opt.value === id) !== -1
              );

              const allChildrenSelected = selectedChildren.length === fullChildIdsForParent.length &&
                fullChildIdsForParent.length > 0;
              const someChildrenSelected = selectedChildren.length > 0 &&
                selectedChildren.length < fullChildIdsForParent.length;
              const isHeaderChecked = selectedHeadersSet.has(headerId) && allChildrenSelected;
              const isHeaderIndeterminate = someChildrenSelected ||
                (selectedHeadersSet.has(headerId) && !allChildrenSelected);
              const isExpanded = this.state.expandedHeaders[headerId];

              return (
                <div key={headerId}>
                  <div style={{ marginBottom: theme.spacing[3] }}>
                    <Checkbox
                      checked={isHeaderChecked}
                      indeterminate={isHeaderIndeterminate}
                      value={headerId}
                      disabled={this.isHeaderDisabled(headerId)}
                      onChange={(e) => this.handleNestedHeaderCheckboxChange(e, headerId, fullChildIdsForParent)}
                    >
                      <b>{t(header.label)}</b>
                    </Checkbox>
                    {isExpanded ? (
                      <CaretDownOutlined
                        onClick={() => this.toggleHeaderExpand(headerId)}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <CaretRightOutlined
                        onClick={() => this.toggleHeaderExpand(headerId)}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                  </div>

                  {isExpanded && allVisibleChildren.map(child => {
                    const isChildChecked = selectedHeadersSet.has(child.value);

                    return (
                      <div style={{ marginBottom: theme.spacing[3], paddingLeft: theme.spacing[6] }} key={child.value}>
                        <Checkbox
                          checked={isChildChecked}
                          value={child.value}
                          disabled={this.isHeaderDisabled(child.value)}
                          onChange={(e) => this.handleNestedHeaderChildCheckboxChange(e, child.value, headerId)}
                        >
                          {t(child.label)}
                        </Checkbox>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {headersWithoutParent.map(header => {
              const isChecked = selectedHeadersSet.has(header.key);

              return (
                <div style={{ marginBottom: theme.spacing[3] }} key={header.key}>
                  <Checkbox
                    checked={isChecked}
                    value={header.key}
                    disabled={this.isHeaderDisabled(header.key)}
                    onChange={(e) => this.onFilterOrActionCheckboxChange(e, itemType)}
                  >
                    {t(header.label)}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
        <div className={classNames(classes.rightDiv, classes.customRightDiv)} >
          <div className={classes.customHeader} >
            <div style={{ textTransform: 'capitalize' }}>{t('details_to_show')}</div>
            <div>{allowMainDashboardSwitch ? t('show_on_main_dashboard') : ''}</div>
          </div>
          <div className={classes.listStyle}>
            {Object.entries(existingHeaderChildMap).map(([headerName, items]) => (
              <div key={headerName} className={classes.headerContainer}>
                <span className={classes.headerTitle}>
                  {t(headerName)}
                </span>
                <this.SortableHeaderList
                  distance={1}
                  items={(items as any)?.map(item => ({ value: item }))}
                  onSortEnd={(params) => this.onNestedHeaderSortEnd({ ...params, collection: headerName })}
                  helperClass="sortableHelper"
                  collection={headerName}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  generateOptionsFromObjectList = (list) => {
    const optionsList: any = [];
    if (list) {
      list.forEach((element) => {
        optionsList.push(<Option value={element.value}>
          {element.name}</Option>);
      });
    }
    return optionsList;
  };
  changeUpdateValue = (val, object, key) => {
    const { updatesActive = [{}] } = this.props;
    const defaultUpdateValues = updatesActive.filter(elem => elem.id === object.id);
    const update = {
     ...defaultUpdateValues[0],
      ...object,
    };
    update[key] = val;
    const items  = this.state.updates.filter(elem => elem.id !== object.id);
    this.setState({ updates: [...items, update]});
  };
  renderUpdates = () => {
    const { classes, onModalSubmit, t } = this.props;
    const { updates } = this.state;
    return (
      <div className={classes.updateDiv}>
        <div>{updates.map((x) => {
          return (
            this.renderUpdate(x)
          );
        })}</div>
        <div>
          <Space>
          <Checkbox
            onChange={(e) => this.setState({enableUpdateForAll: e.target.checked})}>
          </Checkbox>
          {!onModalSubmit && <span>{t('enable_this_setting_on_all_tabs')}</span>}
          </Space>
        </div>
      </div>
    );
  };
  renderUpdate = (update) => {
    const { updatesActive, t } = this.props;
    const activeUpdate = updatesActive.find(element => element.id === update.id);
    switch (update.id) {
      case 'auto_refresh_duration_in_sec':
        return <Space>
          <span>{t(update.label)}</span>
          <Switch
            onChange={(e) => this.changeUpdateValue(e, update, 'isActive')}
            defaultChecked={activeUpdate?.isActive} size="small"
           />
          <Select
            defaultValue={activeUpdate?.value}
            onChange={(e) => this.changeUpdateValue(e, update, 'value')}
            placeholder={t('select_duration')}
          >
            {this.generateOptionsFromObjectList(autoRefreshOptions)}
          </Select>
        </Space>;
      default:
        return null;
    }
  };
  render() {
    const { isVisible, classes, showFiltersAndActions, defaultTab, hideActions,
      updatesToShow, hideFilters, showHeaders, hideColumnTab, t } = this.props;
    const { isUploading , updates} = this.state;
    return (
      <Modal
        title={showFiltersAndActions ? t('customise') : t('customize_columns')}
        visible={isVisible}
        okText={t('update')}
        cancelText={t('cancel')}
        destroyOnClose maskClosable={false}
        width={showFiltersAndActions ? 800 : 700}
        confirmLoading={isUploading}
        onOk={this.handleModalOk}
        onCancel={this.handleModalClose}
        okButtonProps={{ className: classes.customPrimaryButton, disabled: isUploading }}
      >
        {showFiltersAndActions ?
          <Tabs defaultActiveKey={defaultTab || 'column'}>
            {  !hideColumnTab &&
              <TabPane tab={t('columns')} key="column">
                {this.renderColumns()}
              </TabPane>
            }
            {!hideFilters &&
              <TabPane tab={t('filter')} key="filter">
                {this.renderFiltersAndActions('filter')}
              </TabPane>
            }
            {!hideActions &&
              <TabPane tab={t('actions')} key="action">
                {this.renderFiltersAndActions('action')}
              </TabPane>
            }
            {showHeaders &&
              <TabPane tab={t('details')} key="header">
                {this.renderFiltersAndActions('header')}
              </TabPane>
            }
            {updatesToShow.length && <TabPane tab="Updates" key="updates">
                {this.renderUpdates()}
              </TabPane>}
          </Tabs> : this.renderColumns()}
      </Modal>
    );
  }
}

const mapStateToProps = ({ masterData }, ownProps) => {
  const { viewType, bucket } = ownProps;
  const fullFilterList = Array.isArray(masterData.ops_full_filter_list_by_bucket?.[bucket]) ?
    masterData.ops_full_filter_list_by_bucket[bucket] : [];
  const filtersMetadata = Array.isArray(masterData.ops_filter_metadata) ?
  masterData.ops_filter_metadata : [];
  return {
    maxColumnLimit: masterData.dashboard_fetch_max_column_limit || 20,
    columnsToShow: ownProps.columnsToShow && ownProps.columnsToShow ||
      masterData.ops_consignment_column_list[bucket],
    fullColumnList: ownProps.fullColumnList && ownProps.fullColumnList ||
      masterData.ops_full_column_list_by_bucket[bucket],
    filtersToShow: ownProps.filtersToShow ? ownProps.filtersToShow :
      Array.isArray(masterData.ops_filter_list_by_bucket?.[bucket]) ?
      masterData.ops_filter_list_by_bucket[bucket] : [],
    fullFiltersMetadata: ownProps.fullFiltersMetadata ? ownProps.fullFiltersMetadata :
      fullFilterList.map((ele) => {
        return filtersMetadata.filter(item => item.id === ele)[0];
      }),
    fullHeadersList: ownProps.fullHeadersList || [],
    headersToShow:  ownProps.headersToShow || [],
    fullActionsList: ownProps.fullActionsList ? ownProps.fullActionsList :
      Array.isArray(masterData.ops_full_actions_list_by_bucket?.[bucket]) ?
      masterData.ops_full_actions_list_by_bucket[bucket] : [],
    actionsToShow: ownProps.actionsToShow ? ownProps.actionsToShow :
      Array.isArray(masterData.ops_actions_list_by_bucket?.[bucket]) ?
      masterData.ops_actions_list_by_bucket[bucket] : [],
    updatesToShow: ownProps.updatesList ? ownProps.updatesList :
      Array.isArray(masterData.ops_full_keys_list_by_bucket?.[bucket]) ?
      masterData.ops_full_keys_list_by_bucket[bucket] : [],
    updatesActive: ownProps.activeUpdatesList ? ownProps.activeUpdatesList :
      Array.isArray(masterData.ops_keys_list_by_bucket?.[bucket]) ?
      masterData.ops_keys_list_by_bucket[bucket] : [],
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { viewType, bucket } = ownProps;
  return bindActionCreators({
    setColumns : setColumns(viewType)(bucket),
    setCustomFilters: setCustomFilters(viewType)(bucket),
    setCurrentCustomFilters: setCurrentCustomFilters(viewType)(bucket),
    setCustomActions: setCustomActions(viewType)(bucket),
    setCustomUpdates: setCustomUpdates(viewType)(bucket),
    reloadBucket: reloadBucket(viewType)(bucket)
  }, dispatch);
};

const StyledCustomModal = withStyles(styles, { injectTheme: true })(CustomColumnsModal);
export default withTranslation('translation')(connect(mapStateToProps, mapDispatchToProps)(StyledCustomModal));
